// context/ChatContext.js
import React, { createContext, useContext, useState, useEffect, useRef , useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import api from '../services/api';
import notification from "../assets/notification_1.mp3";

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: user?.username || '',
    email: user?.email || ''
  });
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get session ID from localStorage or generate new one
  const getSessionId = () => {
    let sessionId = localStorage.getItem('chat_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_session_id', sessionId);
    }
    return sessionId;
  };

  // Initialize socket connection
  useEffect(() => {
    const sessionId = getSessionId();
    const token = localStorage.getItem('token');
    
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'wss://qr-table.com:9040';
  
    console.log('Connecting to socket:', socketUrl, { sessionId, hasToken: !!token });
  
    const newSocket = io(socketUrl, {
    auth: {
      token: token || null,
      sessionId
    },
    path: '/socket.io', // Make sure this matches server path
    transports: ['websocket', 'polling'],
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    autoConnect: true,
    forceNew: true
  });


    newSocket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
    });

      newSocket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
    setIsConnected(false);
    
    // Try polling if websocket fails
    if (newSocket.io.opts.transports[0] === 'websocket') {
      console.log('Falling back to polling transport');
      newSocket.io.opts.transports = ['polling', 'websocket'];
    }
  });

  newSocket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
    setIsConnected(false);
  });

  newSocket.on('error', (error) => {
    console.error('Socket error:', error);
  });

    newSocket.on('chatStarted', (data) => {
      if (data.success) {
        setCurrentChat(data.chat);
        setMessages(data.chat.messages || []);
        setUnreadCount(0);
      }
    });

    newSocket.on('newMessage', (message) => {
      setMessages(prev => [...prev, message]);
      
      // Play sound if chat is not open
      if (!chatOpen || minimized) {
        playNotificationSound();
        setUnreadCount(prev => prev + 1);
      }
      
      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
    });

    newSocket.on('userTyping', (data) => {
      if (data.role === 'admin') {
        setIsTyping(data.isTyping);
      }
    });

    newSocket.on('chatClosed', () => {
      setCurrentChat(null);
      setMessages([]);
      setChatOpen(false);
    });

    newSocket.on('chatError', (error) => {
      console.error('Chat error:', error);
    });

    newSocket.on('messageError', (error) => {
      console.error('Message error:', error);
    });

    setSocket(newSocket);

    return () => {
    if (newSocket) {
      newSocket.disconnect();
      newSocket.close();
    }
    };
  }, [user]);

  // Load existing chat on mount
  useEffect(() => {
    const loadExistingChat = async () => {
      try {
        const response = await api.get('/chat/my-chat');
        if (response.data) {
          setCurrentChat(response.data);
          setMessages(response.data.messages || []);
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    };
    
    loadExistingChat();
  }, []);

  // Update userInfo when user changes
  useEffect(() => {
    if (user) {
      setUserInfo({
        name: user.username || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio(notification);
      audio.play().catch(() => {
        // Fallback to Web Audio API
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      });
    } catch (error) {
      console.log('Could not play sound');
    }
  };

 
// In context/ChatContext.js - Make sure startChat is defined correctly

const startChat = useCallback(async (name, email, initialMessage = '') => {
  if (!socket || !isConnected) {
    console.error('Socket not connected');
    return false;
  }
  
  return new Promise((resolve, reject) => {
    // Set up one-time listener for response
    const handleChatStarted = (data) => {
      if (data.success) {
        setCurrentChat(data.chat);
        setMessages(data.chat.messages || []);
        setUnreadCount(0);
        resolve(true);
      } else {
        reject(new Error('Failed to start chat'));
      }
    };

    const handleChatError = (error) => {
      reject(new Error(error.message || 'Chat error'));
    };

    // Listen for response
    socket.once('chatStarted', handleChatStarted);
    socket.once('chatError', handleChatError);

    // Emit start chat event
    const restaurantId = localStorage.getItem('current_rest_id_now') || 0;
  
    socket.emit('startChat', {
      name: name || userInfo.name,
      email: email || userInfo.email,
      initialMessage,
      restaurantId // Send restaurant ID
    });

    // Timeout after 10 seconds
    setTimeout(() => {
      socket.off('chatStarted', handleChatStarted);
      socket.off('chatError', handleChatError);
      reject(new Error('Chat start timeout'));
    }, 10000);
  });
}, [socket, isConnected, userInfo]);

  const sendMessage = (message) => {
    if (!socket || !isConnected || !currentChat) {
      console.error('Cannot send message');
      return false;
    }
    
    if (!message.trim()) return false;
    
    socket.emit('sendMessage', {
      chatId: currentChat.id,
      message: message.trim()
    });
    
    return true;
  };

  const sendTyping = (typing) => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('typing', {
      chatId: currentChat.id,
      isTyping: typing
    });
    
    // Clear typing indicator after 2 seconds
    if (typing) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing', {
          chatId: currentChat.id,
          isTyping: false
        });
      }, 2000);
    }
  };

  const markAsRead = () => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('markAsRead', {
      chatId: currentChat.id
    });
    
    setUnreadCount(0);
  };

  const closeChat = () => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('closeChat', {
      chatId: currentChat.id
    });
    
    setCurrentChat(null);
    setMessages([]);
    setChatOpen(false);
  };

  const toggleChat = () => {
    setChatOpen(prev => !prev);
    setMinimized(false);
    if (!chatOpen) {
      markAsRead();
    }
  };

  const toggleMinimize = () => {
    setMinimized(prev => !prev);
  };

  const addAIMessage = useCallback((message) => {
  if (!currentChat) return;
  
  const aiMessage = {
    id: Date.now(),
    chat_id: currentChat.id,
    sender_id: null,
    sender_type: 'ai',
    message: message,
    is_read: true,
    created_at: new Date().toISOString()
  };
  
  setMessages(prev => [...prev, aiMessage]);
}, [currentChat]);


  return (
    <ChatContext.Provider
      value={{
        socket,
        isConnected,
        chatOpen,
        minimized,
        currentChat,
        messages,
        unreadCount,
        isTyping,
        loading,
        userInfo,
        messagesEndRef,
        setUserInfo,
        startChat,
        sendMessage,
        sendTyping,
        markAsRead,
        closeChat,
        toggleChat,
        toggleMinimize,
        setChatOpen,
        addAIMessage
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);